<template>
  <article class="page mooma">
    <router-link to="/" title="Back Home" class="logo logo-absolute">
      <AnimeLogo primaryColor="#FFCC00" />
    </router-link>
    <section
      id="banner"
      class="banner section"
      v-observe-visibility="{
        callback: bannerChanged,
        intersection: { rootMargin: '-204px 0px 0px 0px' }
      }"
    >
      <hr class="stroke mooma-primary" />
      <h1 class="title">{{ content.title }}</h1>
      <h3 class="sub-headline">{{ content.role }}</h3>
      <div class="team">
        <h3 class="headline">Course</h3>
        <ul>
          <li class="padding1-b">SI 616</li>
          <li>
            Dr. Vadim Besprozvanny
            <br />Elena Godin
            <br />
            <span class="small text-second">Mentor</span>
          </li>
        </ul>
      </div>
      <div class="role">
        <h3 class="headline">Role</h3>
        <ul class="list-sum list-mooma">
          <li>
            Visual Identity Design:
            <br />Logo / Iconset / Collaterals / Adaptive visual system.
          </li>
          <li>Website Redesign: Adapt designs to a modern look.</li>
          <li>Mobile App Design: Hi-fi design & prototype.</li>
        </ul>
      </div>
    </section>
    <figure id="mooma-banner-bg" class="banner-bg r-1-3 grid">
      <svg viewBox="0 0 1440 768" class="mooma-banner-pic">
        <title>MoOMA - Banner Picture</title>
        <g class="recenter">
          <g class="isometric layer-1">
            <image
              id="mooma-web-home-hover"
              x="518"
              y="0"
              width="540"
              height="970.25"
              href="@/assets/work/mooma/thumbnail/home-hover.png"
            />
            <rect x="518" y="529.5" width="1200" height="135" fill="#FFCC00" />
            <rect x="518" y="754.5" width="135" height="960" fill="#CC0066" />
          </g>
          <g class="isometric layer-2">
            <image
              id="mooma-mobile-onboard"
              x="518"
              y="664.5"
              width="135"
              height="270"
              transform="rotate(-90,518,664.5)"
              href="@/assets/work/mooma/thumbnail/onboard.png"
            />
            <image
              id="mooma-mobile-artwork"
              x="518"
              y="754.5"
              width="135"
              height="337.5"
              href="@/assets/work/mooma/thumbnail/artwork.png"
            />
          </g>
        </g>
      </svg>
      <img
        src="@/assets/work/mooma/banner/mooma-logo.svg"
        alt="MoOMA Logo"
        class="banner-logo"
      />
    </figure>
    <section id="overview" class="overview r-2 section">
      <hr class="divider" />
      <a
        href="#overview"
        class="outline theme-mooma sticky"
        v-scroll-to="'#overview'"
        >Overview</a
      >
      <h2 class="headline">MoOMA Visual Identity Refresh</h2>
      <h4 class="subtitle">Client Project Brief</h4>
      <p class="content">
        This is a visual-focused client project as a part of a design studio
        course where we were tasked to design individually a new comprehensive
        visual identity for the client Museum of Odessa Modern Art throughout
        the semester. Under the supervision and guidance of my mentors, I
        delivered the final designs with a brand book, a website refresh with
        component guidelines and a mobile app concept with a hi-fi prototype.
      </p>
      <p class="content">
        My design was later presented to the client with several other designs
        from the course and was
        <strong class="highlight">selected</strong> as their
        <strong class="highlight">new visual identity concept</strong>.
      </p>
    </section>
    <section id="mooma-visual" class="mooma-visual section">
      <hr class="divider" />
      <h2 class="headline no-subtitle">Visual Identity Design</h2>
      <p class="content">
        My process of redesigning the visual identity of the museum started with
        a detailed look into the form and theme of the existing logo; then
        distilled the design motif to continue and expand on it.
      </p>
      <div class="mooma-visual min-width">
        <object type="image/svg+xml" :data="svgLib.visual" id="mooma-visual" />
      </div>
      <p class="content">
        My design philosophy for the new visual identity can be summed up as the
        following themes:
      </p>
      <ol class="content">
        <li>
          <strong class="highlight">Deconstruct</strong> the visual identity
          into simple design motifs. The existing visual identity is highly
          geometric. Therefore, I used broke the logo into simplified geometric
          elements that can be used across web, mobile and collateral designs.
        </li>
        <li>
          <strong class="highlight">Continuation</strong> of the style while
          simplifying it. The existing color palette was simplified and tiered
          into two categories to create a cleaner style while retaining its
          extremely vibrant color palette as secondary colors.
        </li>
        <li>
          <strong class="highlight">Adapt</strong> the visual identity to an
          inherently multi-lingual use case. The museum itself is trilingual
          with equal importance. Therefore, I created a shared visual identity
          that's trilingual by nature and flow among the three as you switch
          languages.
        </li>
      </ol>
      <iframe
        src="https://drive.google.com/file/d/1-IGJRQuNdM1jL4JS5YZZnBWNe5o0-XC-/preview"
        class="mooma-brand-book c-1--1 margin2"
      />
    </section>
    <section id="mooma-web" class="mooma-web section">
      <hr class="divider" />
      <a
        href="#mooma-web"
        class="outline theme-mooma sticky"
        v-scroll-to="'#mooma-web'"
        >Website</a
      >
      <h2 class="headline">Website Redesign</h2>
      <h4 class="subtitle">Design for a Multilingual Site</h4>
      <p class="content">
        Being a multilingual website by nature, a key part of my design for the
        museum website was to adapt my design to such an environment. I chose
        the header section of the website to manifest the fluid visual identity
        that adapts as site language change helped by motion design.
      </p>
      <div id="mooma-video" class="mooma-video grid">
        <figure class="video-fig">
          <div class="crop-top aspect a5-1">
            <video
              class="top"
              poster="@/assets/work/mooma/video/mooma-header.png"
              src="@/assets/work/mooma/video/mooma-header.mp4"
              autoplay
              playsinline
              muted
              loop
            >
              Your browser does not support video tag.
            </video>
          </div>
          <figcaption class="xsmall text-second">
            <strong>Website Header</strong>
            <br />Header section adapts to language change to show the
            corresponding part of the logo.
          </figcaption>
        </figure>
      </div>
      <p class="content">
        The website redesign includes home, about, art collections and events
        page. The main focus was to create a clean and modern website that could
        showcase the amazing arts and exhibitions at the venue directly on their
        website.
      </p>
      <p class="content">
        With that in mind, I created the website mock-up below.
      </p>
      <div class="mooma-web-proto desktop-bezel margin2">
        <iframe
          src="https://xd.adobe.com/embed/0a455416-35f0-47d7-5f83-df1c72e40368-7c74/"
          frameborder="0"
          class="screen"
          allowfullscreen
        />
        <img
          src="@/assets/img/prototype/screen-bezel.svg"
          alt="Desktop screen bezel"
          class="bezel"
        />
      </div>
    </section>
  </article>
</template>

<style lang="scss">
@import '@/styles/base.scss';

.mooma {
  .banner-bg {
    padding-top: 0;

    .recenter {
      transform: translate(0, 360px);
    }

    .isometric.layer-1 {
      transform: rotateX(60deg) rotateY(0deg) rotateZ(-45deg);
    }
    .isometric.layer-2 {
      transform: translate(0, -24px) rotateX(60deg) rotateY(0deg)
        rotateZ(-45deg);
    }
  }

  .highlight:before {
    background: rgba($mooma-second, 0.5);
  }

  figcaption:before {
    background-color: $mooma-second;
  }
}

.mooma-primary {
  background-color: $mooma-primary;

  &.stroke {
    grid-column-start: 4;
    grid-row-start: 1;
    width: 100%;
    position: relative;
    top: $logo-pad-top + $page-padding-top - $banner-padding-top;
  }
}

.mooma-banner-pic {
  grid-column: 1 / -1;
  min-width: $small-width * 0.8;
}

.mooma-visual {
  grid-column: 1 / -1;
}
iframe.mooma-brand-book {
  height: $book-height;
  width: 100%;
  max-height: 100vh;
}

.mooma-web-proto {
  grid-column: 1 / -1;
}

.desktop-bezel {
  position: relative;
  height: 0;
  padding-top: 768 / 1344 * 100%;

  & > * {
    position: absolute;
  }

  .screen {
    width: 1024 / 1344 * 100%;
    height: 640 / 768 * 100%;
    left: 160 / 1344 * 100%;
    top: 56 / 768 * 100%;
  }
  .bezel {
    pointer-events: none;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
  }
}

.mooma-video {
  grid-column: 1 / -1;

  .video-fig {
    grid-column: 1 / -1;
    z-index: -1;

    figcaption {
      padding: $baseline $column-gap 0;
    }
  }

  .crop-top {
    overflow-y: hidden;
  }

  video {
    width: 100%;
  }
}

// Media query
@media only screen and (min-width: $small-width) {
  .mooma-primary.stroke {
    grid-column-start: 3;
  }
  .mooma-video {
    .video-fig {
      grid-column: 3 / span 3;

      figcaption {
        padding: $baseline 0 0;
      }
    }
  }
  .mooma-web-proto {
    grid-column: 2 / -2;
  }
}
@media only screen and (min-width: $mid-width) {
  .mooma-video {
    .video-fig {
      grid-column-start: 4;
    }
  }
}
@media only screen and (min-width: $large-width) {
  .mooma-video {
    .video-fig {
      grid-column: 5 / -3;
    }
  }
}
</style>

<script>
import AnimeLogo from '@/components/animation/logo.vue'

import anime from 'animejs'

export default {
  name: 'mooma',
  data() {
    return {
      content: this.$store.state.work.mooma.content,
      bannerVisible: null,
      darkVisible: null,
      prevHeight: 0,
      iterationZoomed: true,
      svgLib: {
        visual: require('@/assets/work/mooma/content/visual-system.svg')
      }
    }
  },
  components: {
    AnimeLogo
  },
  methods: {
    bannerChanged(isVisible, entry) {
      if (this.bannerVisible === null || this.bannerVisible !== isVisible) {
        this.bannerVisible = isVisible
        // console.log('banner visible: ' + this.bannerVisible)
        this.$emit('banner', this.bannerVisible, entry)
      }
    },
    darkSection(isVisible, entry) {
      if (this.darkVisible === null || this.darkVisible !== isVisible) {
        this.darkVisible = isVisible
        // console.log('dark visible: ' + this.darkVisible)
        this.$emit('darksec', this.darkVisible, entry)
      }
    },
    beforeLeave(element) {
      this.prevHeight = getComputedStyle(element).height
      // console.log(this.prevHeight)
    },
    enter(element) {
      const enterHeight = getComputedStyle(element).height

      element.style.height = this.prevHeight
      // console.log(enterHeight)

      anime({
        targets: element,
        height: enterHeight,
        easing: 'easeInOutQuad',
        duration: 500
      }).play()
    },
    afterEnter(element) {
      element.style.height = 'auto'
    },
    toggleIterationView(zoomed) {
      this.iterationZoomed = zoomed
    }
  },
  mounted() {
    this.$store.commit('changeWorkLinkColor', '#CC0066')
  }
}
</script>
